import { useState } from "react";
import { Dumbbell, Repeat, Timer, Weight, ChevronDown, PlayCircle, Lightbulb, AlertTriangle, Trash2, TrendingUp } from "lucide-react";
import { muscleImage, ytEmbed, ytSearch, MUSCLE_LIST } from "@/data/exercises";
import { muscleName, tr } from "@/data/i18n";

export function ExerciseCard({ exercise, beginner, lang, index, editable, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const show = (beginner || open) && !editable;

  const upd = (field, value) => onChange && onChange({ ...exercise, [field]: value });

  return (
    <div className="print-card rounded-2xl border border-zinc-800 bg-zinc-900/50 overflow-hidden transition-all hover:border-red-500/30"
         data-testid="exercise-item-card">
      <div className="p-5 flex items-start gap-4">
        <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-400 font-display text-xl font-bold">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            {editable ? (
              <input value={exercise.name} onChange={(e) => upd("name", e.target.value)}
                     className="flex-1 bg-transparent font-heading font-bold text-lg text-white outline-none border-b border-transparent focus:border-red-500"
                     data-testid="exercise-name-input" />
            ) : (
              <h4 className="font-heading font-bold text-lg text-white truncate">{exercise.name}</h4>
            )}
            {editable && (
              <button onClick={onRemove} className="no-print text-zinc-500 hover:text-red-500 transition-colors" data-testid="remove-exercise-btn">
                <Trash2 size={18} />
              </button>
            )}
          </div>
          {editable ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select value={MUSCLE_LIST.find((m) => (exercise.primary_muscle || "").toLowerCase().includes(m.toLowerCase())) || "Petto"}
                      onChange={(e) => upd("primary_muscle", e.target.value)} data-testid="exercise-muscle-select"
                      className="appearance-none rounded-full border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-semibold text-orange-300 outline-none focus:border-orange-500">
                {MUSCLE_LIST.map((m) => <option key={m} value={m} className="bg-zinc-900">{muscleName(m, lang)}</option>)}
              </select>
              <input value={exercise.equipment || ""} onChange={(e) => upd("equipment", e.target.value)} placeholder={tr(lang, "equipment")}
                     data-testid="exercise-equipment-input"
                     className="rounded-full border border-zinc-700 bg-zinc-800 px-3 py-1 text-xs text-zinc-200 outline-none focus:border-red-500" />
            </div>
          ) : (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2.5 py-0.5 text-xs font-semibold text-orange-400">
              {muscleName(exercise.primary_muscle, lang)}
            </span>
            {exercise.equipment && (
              <span className="rounded-full bg-zinc-800 px-2.5 py-0.5 text-xs text-zinc-400">{exercise.equipment}</span>
            )}
          </div>
          )}

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat icon={<Dumbbell size={14} />} label={tr(lang, "sets")} value={exercise.sets} editable={editable} onChange={(v) => upd("sets", v)} />
            <Stat icon={<Repeat size={14} />} label={tr(lang, "reps")} value={exercise.reps} editable={editable} onChange={(v) => upd("reps", v)} />
            <Stat icon={<Timer size={14} />} label={tr(lang, "rest")} value={exercise.rest} editable={editable} onChange={(v) => upd("rest", v)} />
            <Stat icon={<Weight size={14} />} label={tr(lang, "load")} value={exercise.load} editable={editable} onChange={(v) => upd("load", v)} />
          </div>

          {editable && (
            <div className="mt-3">
              <label className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500"><TrendingUp size={12} /> {tr(lang, "progression_lbl")}</label>
              <textarea value={exercise.notes || ""} onChange={(e) => upd("notes", e.target.value)} rows={2} placeholder={tr(lang, "progression_ph")}
                        data-testid="exercise-notes-input"
                        className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-red-500" />
            </div>
          )}
          {!editable && exercise.notes && (
            <div className="mt-2 flex items-start gap-1.5 text-sm text-zinc-400"><TrendingUp size={14} className="mt-0.5 shrink-0 text-red-400" /> {exercise.notes}</div>
          )}

          {!beginner && !editable && (exercise.execution?.length > 0 || exercise.video_query) && (
            <button onClick={() => setOpen(!open)} className="no-print mt-3 inline-flex items-center gap-1 text-sm font-medium text-red-400 hover:text-red-300 transition-colors" data-testid="exercise-details-toggle">
              <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
              {tr(lang, "execution")}
            </button>
          )}
        </div>
      </div>

      {show && (
        <div className="border-t border-zinc-800 bg-zinc-950/40 p-5 animate-float-up">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-4">
              {exercise.execution?.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-400">{tr(lang, "execution")}</p>
                  <ol className="space-y-1.5">
                    {exercise.execution.map((s, i) => (
                      <li key={i} className="flex gap-2 text-sm text-zinc-300">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500/20 text-[11px] font-bold text-red-400">{i + 1}</span>
                        {s}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {exercise.beginner_tip && (
                <div className="flex gap-2 rounded-xl bg-emerald-500/10 p-3 text-sm text-emerald-300">
                  <Lightbulb size={16} className="mt-0.5 shrink-0" />
                  <div><b className="font-semibold">{tr(lang, "tip")}:</b> {exercise.beginner_tip}</div>
                </div>
              )}
              {exercise.common_mistakes && (
                <div className="flex gap-2 rounded-xl bg-amber-500/10 p-3 text-sm text-amber-300">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div><b className="font-semibold">{tr(lang, "mistakes")}:</b> {exercise.common_mistakes}</div>
                </div>
              )}
            </div>

            <div className="space-y-4 print-hide-media">
              <div className="flex gap-3">
                <div className="w-1/3 shrink-0">
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-zinc-400">{tr(lang, "muscle_worked")}</p>
                  <img src={muscleImage(exercise.primary_muscle)} alt={exercise.primary_muscle}
                       className="aspect-square w-full rounded-xl object-cover border border-zinc-800" />
                </div>
                <div className="flex-1">
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-zinc-400">{tr(lang, "video")}</p>
                  <div className="aspect-video w-full overflow-hidden rounded-xl border border-zinc-800 bg-black">
                    <iframe title={exercise.name} src={ytEmbed(exercise.video_query || exercise.name)}
                            className="h-full w-full" allowFullScreen frameBorder="0" />
                  </div>
                  <a href={ytSearch(exercise.video_query || exercise.name)} target="_blank" rel="noreferrer"
                     className="mt-1.5 inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300">
                    <PlayCircle size={13} /> YouTube
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, editable, onChange }) {
  return (
    <div className="rounded-lg bg-zinc-800/50 px-2.5 py-1.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-zinc-500">{icon}{label}</div>
      {editable ? (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
               className="w-full bg-transparent font-mono text-sm font-bold text-white outline-none" />
      ) : (
        <div className="font-mono text-sm font-bold text-white">{value || "-"}</div>
      )}
    </div>
  );
}
